import PropTypes from "prop-types";
import { useId } from "react";

/**
 * Animated walk-cycle version of the Uno sheep logo.
 *
 * The body is a legless variant of the Uno path: the four baked-in legs were
 * spliced out of the original contour and each gap re-closed with one smooth
 * cubic; the negative-space face subpath is untouched. Four authored two-segment
 * limbs (thigh + shin "capsules" — round-capped lines in the logo's leg style)
 * are layered BEHIND the body. The body's belly sits at ~y23 while each hip
 * pivot rides high at ~y21, tucked behind the body — so no mask or cover is
 * needed and the negative-space face reads correctly with nothing over it.
 *
 * Each leg is DRAWN in its rest stance (hip high under the belly, hoof reaching
 * out to the logo's actual hoof position) so the hip can stay hidden while the
 * hoof lands where the logo's does — the leg's bend decouples the two. The
 * animateTransforms add the stride on top: hips swing, knees flex through the
 * swing phase, and the whole figure bobs and tilts. SMIL drives the SVG
 * timeline, so pauseAnimations()/setCurrentTime() scrub it.
 */

// Legless Uno body. First subpath: the original wool/belly contour with the four
// legs spliced out and the gaps re-closed by two smooth cubics (the absolute `C`
// commands). Second subpath (m10.56 7.58…): the untouched negative-space face.
// Every other command is byte-for-byte the original, so silhouette and face match.
const BODY_PATH =
  "M14.96 4.72h.96c.02.01.05.03.07.03.31.04.6.13.89.25.25.1.49.23.7.39.21.16.4.34.6.52.04.03.07.05.11.02.23-.15.47-.27.73-.35.26-.09.53-.14.81-.15.4-.02.78.03 1.16.16.28.09.54.22.77.38.56.39.95.91 1.18 1.55.02.06.05.08.11.07.58-.03 1.11.1 1.62.38.41.22.75.53 1.03.91.33.46.52.97.56 1.53.01.14 0 .29 0 .43 0 .05 0 .09.06.11.1.03.19.07.29.11.27.11.52.26.74.45.58.49.95 1.11 1.08 1.86.13.71.01 1.39-.33 2.03-.21.38-.49.71-.84.98-.05.03-.06.07-.04.13l.09.38c.12.66.02 1.28-.26 1.88-.21.45-.52.82-.92 1.12C24.53 21.09 22.082 23.638 20.53 23.25c-.2-.05-.4-.08-.6-.13-.08-.02-.13 0-.19.04-.12.07-.24.15-.36.21-.22.12-.46.21-.71.25-.24.04-.48.08-.73.08-.23 0-.47-.05-.7-.09-.25-.05-.49-.14-.7-.27-.2-.12-.38-.26-.58-.39-.03-.02-.07-.03-.1-.02-.27.09-.54.14-.83.17-.43.04-.86 0-1.27-.13-.17-.05-.33-.12-.49-.17-.04-.01-.09 0-.12.01C10.274 23.595 6.692 22.564 5.77 21.51c-.14-.16-.27-.32-.41-.48-.03-.04-.08-.07-.13-.1-.16-.08-.33-.15-.49-.24-.45-.26-.84-.58-1.17-.99a4.25 4.25 0 0 1-.71-1.24c-.1-.27-.16-.54-.2-.83-.06-.4-.06-.8 0-1.2.04-.25.09-.5.18-.74.01-.03 0-.07 0-.1-.14-.33-.24-.68-.29-1.03 0-.03-.03-.06-.04-.09v-1.03s.04-.07.04-.1c.07-.5.23-.96.47-1.4.28-.51.65-.95 1.12-1.3.3-.23.63-.42.98-.56.1-.04.11-.1.14-.18.08-.22.15-.44.24-.65.17-.4.42-.75.71-1.07.27-.3.57-.55.92-.75.21-.12.42-.22.64-.32.14-.06.29-.1.44-.16.03-.01.07-.05.08-.08.11-.22.23-.44.39-.63.5-.62 1.14-1.01 1.93-1.15.4-.07.8-.06 1.19.03.39.09.76.24 1.09.48.06.04.09.04.14 0 .32-.28.68-.49 1.08-.65.25-.1.5-.17.76-.2.03 0 .07-.03.1-.04z m10.56 7.58s.02-.1 0-.13c-.07-.1-.15-.18-.27-.2-.1-.02-.2-.03-.3-.05-.05 0-.11 0-.16-.02-.15-.06-.3-.05-.45-.05-.34-.01-.68-.03-1.01.06-.1.02-.19.04-.29.08-.07.03-.14.07-.21.1-.09.03-.19.05-.28.07-.09.01-.18.02-.27.02a1.001 1.001 0 0 1-.47-.15c-.09-.05-.17-.09-.26-.14-.08-.05-.16-.11-.25-.14-.2-.08-.39-.16-.59-.23h-.04c-.14-.01-.28-.08-.43-.07-.03 0-.06-.01-.09-.01-.19 0-.38-.05-.56-.02-.18.03-.36.01-.53.09-.03.02-.07 0-.11.02-.08.03-.16.07-.24.1-.07.03-.15.06-.21.09-.14.08-.28.16-.41.26-.15.11-.29.22-.42.35-.12.13-.24.25-.34.4-.12.18-.24.37-.33.57-.02.05-.04.1-.08.14-.07.1-.14.2-.23.28-.08.08-.18.15-.29.2-.08.04-.19.04-.28.08-.14.06-.31.02-.45.11h-.03c-.08.02-.16.04-.23.07-.07.03-.13.06-.19.09-.09.05-.19.08-.27.14-.22.15-.44.3-.65.47-.15.12-.28.25-.4.39-.05.06-.1.14-.08.24.04.12.1.21.21.28.15.09.31.18.47.27.05.03.1.05.16.07.08.03.16.05.24.08.11.04.22.08.34.1.15.02.3.03.45.03.14 0 .28-.02.42-.03.09 0 .18 0 .26-.05.01 0 .03-.01.05-.02.1-.02.2-.03.3-.07.13-.05.26-.11.4-.12.16-.02.31 0 .41.13.09.11.15.24.19.37.03.1.07.19.1.28.03.08.05.16.08.23.02.07.05.14.08.21.03.07.07.14.1.21.05.11.08.23.13.34a7.12 7.12 0 0 0 .32.64c.04.08.09.16.13.23.08.14.17.29.26.42.07.1.15.2.23.3.13.16.26.33.41.48.1.1.21.2.32.29.11.09.23.16.36.24.08.05.17.09.26.12.09.04.18.08.28.1.11.02.23.03.34.03.11 0 .22-.01.32-.03.11-.02.22-.04.32-.07.1-.04.19-.1.28-.15.18-.1.34-.22.46-.4.06-.08.11-.16.17-.24.06-.09.11-.18.16-.28.04-.08.06-.18.09-.27.03-.09.06-.18.09-.28.02-.08.04-.17.05-.25l.03-.32c.01-.09.04-.18.04-.27V16.98c-.02-.14-.03-.28-.05-.42l-.03-.25c0-.06-.02-.13-.03-.19-.02-.15-.05-.29-.08-.44-.03-.13-.07-.26-.1-.39s-.04-.27-.07-.4c-.03-.12 0-.23.02-.34 0-.01.01-.03.02-.04.06-.05.12-.12.19-.16.08-.05.18-.07.27-.1.11-.03.22-.04.33-.07.08-.02.17-.06.24-.09.16-.07.31-.13.46-.22.14-.08.26-.17.39-.27.21-.16.38-.37.52-.6.07-.11.15-.22.2-.34.06-.13.15-.25.13-.4z";

// The muzzle as a standalone shape, used to clip the limbs so none show THROUGH
// the negative-space face. It's BODY_PATH's second subpath, which is written
// relative to the first subpath's start (14.96,4.72) — so its "m10.56 7.58"
// resolves to an absolute 25.52,12.3 when lifted out on its own.
const FACE_PATH = `M25.52 12.3${BODY_PATH.split("z")[1]
  .trim()
  .slice("m10.56 7.58".length)}`;

const colors = {
  white: "#fff",
  blue: "#15436A",
  lightBlue: "#278ec2",
};

// One stride, shared by every leg and phase-shifted via negative `begin`. The
// keyTimes are asymmetric — a long planted stance (0→0.55, thigh rotating the
// hoof back) then a quick swing (0.55→1, knee folds, leg lifts and reaches
// forward).
const KEY_TIMES = "0;0.55;0.72;0.86;1";

// Thigh and shin need DIFFERENT easing through the swing, so they can't share one
// spline set. A shared ease-in-out brakes BOTH to zero velocity at every keyframe,
// which reads as a visible stop-and-go jerk. The fix is to let each joint flow
// through the keyframes where its own value is still moving the same direction, and
// only brake at its true turning points.
//
// The thigh sweeps monotonically across the whole swing (23→14→-9→-23), so its only
// turning points are the stance ends (0.55 and 0/1). It eases in/out there but
// carries momentum straight through the mid-swing keyframes (0.72, 0.86) — strong
// ease-in out of 0.55, linear through the middle, ease-out into the 1.0 reversal.
const THIGH_SPLINES = "0.4 0 0.6 1;0.5 0 1 1;0 0 1 1;0 0 0.6 1";
// The shin DOES reverse mid-swing — it peaks at 0.72 (0→45→22→0) — so it brakes
// there, then flows through 0.86 (still unfolding the same direction) into rest.
const SHIN_SPLINES = "0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 1 1;0 0 0.6 1";

// Thigh angle about the hip (+ swings the hoof backward).
const thighValues = (hipX, hipY) =>
  [-23, 23, 14, -9, -23].map((a) => `${a} ${hipX} ${hipY}`).join(";");

// Shin angle about the knee (+ folds the shin up): ~0 through stance, a big flex
// at lift so the hoof clears the ground, easing back to 0 by touchdown.
const shinValues = (kneeX, kneeY) =>
  ["0", "0", "45", "22", "0"].map((a) => `${a} ${kneeX} ${kneeY}`).join(";");

/**
 * Four limbs in their logo rest stance. Hips ride high under the belly; within
 * each pair one leg is planted long (the "near" leg) and the other is raised with
 * a folded knee (short, the "far" leg, foreshortened in the logo).
 *
 * This is nudged toward how a real quadruped differs front-to-back, NOT a clean
 * left-right mirror:
 *  - Shape: the HIND legs keep their pronounced backward knee/hock zig-zag; the
 *    FORE legs are straighter, column-like posts with the knee only tucked slightly
 *    rearward — a foreleg reads as a strut, not a spring.
 *  - Amplitude: the fore legs sweep less than the hind, which we get for free. The
 *    whole-figure tilt see-saws opposite the front swing and damps it to ~⅔; we no
 *    longer over-compensate for that (the old tAmp 1.5). Hind drives, fore supports.
 *  - Timing: `phase` (× dur, via begin) runs the textbook 4-beat LATERAL-sequence
 *    walk — each same-side fore foot trails its hind by a quarter stride
 *    (rear-near 0 → front-near .25 → rear-far .5 → front-far .75). No two feet land
 *    together; that even single-file stagger is what reads as a walk.
 *
 * The clip keeps any hip/cap that lands inside the negative-space muzzle from
 * showing through (see the render's clipPath).
 */
const LEG_WIDTH = 2.4;

// Hoof = the leg's far endpoint; the round cap adds LEG_WIDTH/2 beyond it, so each
// `hoof.y` sits near the logo's measured hoof-tip MINUS that cap radius, landing the
// rendered tip on the logo's tip. The far legs are genuinely shorter (foreshortened
// in the logo), so those tuck up close to the belly.

const LEGS = [
  // rear (hind) pair — the approved deep backward knee/hock zig-zag.
  {
    id: "rear-far",
    hip: { x: 6.8, y: 20.9 },
    knee: { x: 6.4, y: 22.4 },
    hoof: { x: 6.0, y: 23.55 },
    phase: 0.5,
  },
  {
    id: "rear-near",
    hip: { x: 8.6, y: 20.9 },
    knee: { x: 8.75, y: 23.2 },
    hoof: { x: 8.9, y: 25.05 },
    phase: 0,
  },
  // front (fore) pair — straighter posts, knee tucked only slightly rearward (not
  // the hind's deep zig-zag, and not a mirror of it). Each trails its same-side hind
  // by a quarter stride for the lateral-sequence walk.
  {
    id: "front-far",
    hip: { x: 24.2, y: 20.9 },
    knee: { x: 24.0, y: 22.3 },
    hoof: { x: 24.15, y: 23.5 },
    phase: 0.75,
  },
  {
    id: "front-near",
    hip: { x: 22.4, y: 20.9 },
    knee: { x: 22.2, y: 22.95 },
    hoof: { x: 22.4, y: 24.95 },
    phase: 0.25,
  },
];

/**
 * One articulated limb: thigh capsule pivoting at the hip, shin capsule pivoting
 * at the knee. Coordinates are the rest pose; the nested animateTransforms add
 * the stride swing so the joint rotations compose.
 */
function Leg({ hip, knee, hoof, width, stroke, dur, begin }) {
  return (
    <g>
      <animateTransform
        attributeName="transform"
        type="rotate"
        additive="sum"
        values={thighValues(hip.x, hip.y)}
        keyTimes={KEY_TIMES}
        keySplines={THIGH_SPLINES}
        calcMode="spline"
        dur={`${dur}s`}
        begin={`${begin}s`}
        repeatCount="indefinite"
      />
      <line
        x1={hip.x}
        y1={hip.y}
        x2={knee.x}
        y2={knee.y}
        stroke={stroke}
        strokeWidth={width}
        strokeLinecap="round"
      />
      <g>
        <animateTransform
          attributeName="transform"
          type="rotate"
          additive="sum"
          values={shinValues(knee.x, knee.y)}
          keyTimes={KEY_TIMES}
          keySplines={SHIN_SPLINES}
          calcMode="spline"
          dur={`${dur}s`}
          begin={`${begin}s`}
          repeatCount="indefinite"
        />
        <line
          x1={knee.x}
          y1={knee.y}
          x2={hoof.x}
          y2={hoof.y}
          stroke={stroke}
          strokeWidth={width}
          strokeLinecap="round"
        />
      </g>
    </g>
  );
}

Leg.propTypes = {
  hip: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }).isRequired,
  knee: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number })
    .isRequired,
  hoof: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number })
    .isRequired,
  width: PropTypes.number.isRequired,
  stroke: PropTypes.string.isRequired,
  dur: PropTypes.number.isRequired,
  begin: PropTypes.number.isRequired,
};

UnoWalking.propTypes = {
  color: PropTypes.oneOf(["white", "blue", "lightBlue"]),
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Seconds for one full stride. Lower = faster gait. */
  dur: PropTypes.number,
};

export default function UnoWalking({ color = "white", size = 200, dur = 1.1 }) {
  const fill = colors[color];
  const clipId = useId();

  return (
    <svg viewBox="0 0 31 31" width={size} height={size}>
      <defs>
        {/* Clip the limbs to everything EXCEPT the muzzle. The legs sit behind the
            body, but the face is negative space (a hole), so a leg swinging up
            behind it would otherwise show THROUGH. evenodd: the big rect minus the
            face leaves the face uncovered, hiding any limb there — the true page
            background shows through the hole on any backdrop. */}
        <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
          <path d={`M-9 -9H40V40H-9Z ${FACE_PATH}`} clipRule="evenodd" />
        </clipPath>
      </defs>
      {/* Whole figure bobs (twice per stride) and tilts, about the body center. */}
      <g>
        <animateTransform
          attributeName="transform"
          type="translate"
          additive="sum"
          values="0 0;0 -0.9;0 0;0 -0.9;0 0"
          keyTimes="0;0.25;0.5;0.75;1"
          keySplines="0.45 0 0.55 1;0.45 0 0.55 1;0.45 0 0.55 1;0.45 0 0.55 1"
          calcMode="spline"
          dur={`${dur}s`}
          repeatCount="indefinite"
        />
        <animateTransform
          attributeName="transform"
          type="rotate"
          additive="sum"
          values="-2.5 15.5 15.5;2.5 15.5 15.5;-2.5 15.5 15.5"
          keyTimes="0;0.5;1"
          keySplines="0.45 0 0.55 1;0.45 0 0.55 1"
          calcMode="spline"
          dur={`${dur}s`}
          repeatCount="indefinite"
        />

        {/* Limbs behind the body, clipped so none show through the muzzle. */}
        <g clipPath={`url(#${clipId})`}>
          {LEGS.map(({ id, hip, knee, hoof, phase }) => (
            <Leg
              key={id}
              hip={hip}
              knee={knee}
              hoof={hoof}
              width={LEG_WIDTH}
              stroke={fill}
              dur={dur}
              begin={-phase * dur}
            />
          ))}
        </g>

        {/* Legless body + negative-space face, drawn on top of the limbs. */}
        <path d={BODY_PATH} style={{ fill }} />
      </g>
    </svg>
  );
}
